import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { AnimatedLoginButton } from "@/components/animated-login-button"
import { AuthBackToSignInLink } from "@/components/auth/auth-back-to-sign-in-link"
import { API_ORIGIN, CURRENT_CLIENT } from "@/constants"
import { allauthJsonHeaders } from "@/lib/allauth-csrf"

interface PasswordResetConfirmProps extends React.ComponentPropsWithoutRef<"div"> {
  onSuccess?: () => void;
}

export function PasswordResetConfirm({ className, onSuccess, ...props }: PasswordResetConfirmProps) {
  const { key } = useParams<{ key: string }>()
  const [password, setPassword] = useState("")
  const [passwordConfirm, setPasswordConfirm] = useState("")
  const [error, setError] = useState("")
  const [errorField, setErrorField] = useState<"password" | "confirm" | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  useEffect(() => {
    if (!key) {
      setError("Invalid reset link")
    }
  }, [key])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setErrorField(null)

    if (!password || !passwordConfirm) {
      setError("Please fill in all fields")
      setErrorField(!password ? "password" : "confirm")
      return
    }

    if (password !== passwordConfirm) {
      setError("Passwords do not match")
      setErrorField("confirm")
      return
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long")
      setErrorField("password")
      return
    }

    if (!key) {
      setError("Invalid reset link")
      return
    }

    setIsLoading(true)

    try {
      // Allauth headless expects key (uidb36-token format) and password
      // The key from the email link should be sent as-is
      // Format: {uidb36}-{token} where uidb36 is user ID in base36
      const requestBody = {
        key,
        password
      }

      const headers = await allauthJsonHeaders()

      const response = await fetch(`${API_ORIGIN}/_allauth/browser/v1/auth/password/reset`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(requestBody),
      })

      const data = await response.json()

      // Check if this is a successful password reset (401 AuthenticationResponse from allauth)
      // Allauth returns 401 with {"status":401,"data":{"flows":[...]}} to indicate
      // password reset successful, user needs to authenticate
      const isSuccessfulAuthResponse = response.status === 401 && 
        data.status === 401 && 
        data.data && 
        Array.isArray(data.data.flows) &&
        data.data.flows.length > 0
      
      if (response.ok || isSuccessfulAuthResponse) {
        // Success - redirect to login
        navigate('/login', { state: { message: 'Password reset successfully! Please login with your new password.' } })
        onSuccess?.()
      } else {
        // Log the full error response for debugging
        console.error('Password reset error response:', data)
        console.error('Response status:', response.status)
        
        // Allauth returns errors in different formats - check common fields
        // Sometimes errors are in data.errors or data.input.errors
        let errorMessage = 'Failed to reset password'
        
        if (data.error) {
          errorMessage = data.error
        } else if (data.detail) {
          errorMessage = data.detail
        } else if (data.message) {
          errorMessage = data.message
        } else if (data.errors && Array.isArray(data.errors)) {
          // Handle errors array format: [{"message":"...","code":"...","param":"..."}]
          const firstError = data.errors[0]
          if (firstError && firstError.message) {
            errorMessage = firstError.message
            // If token is invalid, suggest requesting a new one
            if (firstError.code === 'invalid_password_reset') {
              errorMessage += '. The password reset link may have expired. Please request a new password reset.'
            }
          }
        } else if (data.errors && typeof data.errors === 'object') {
          // Handle nested errors object
          const errorKeys = Object.keys(data.errors)
          if (errorKeys.length > 0) {
            const firstError = data.errors[errorKeys[0]]
            errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
          }
        } else if (data.input && data.input.errors) {
          // Handle input.errors format
          const errorKeys = Object.keys(data.input.errors)
          if (errorKeys.length > 0) {
            const firstError = data.input.errors[errorKeys[0]]
            errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
          }
        } else {
          // Fallback: show full response
          errorMessage = JSON.stringify(data)
        }
        
        setError(errorMessage)
      }
    } catch (error) {
      console.error('Password reset confirm error:', error)
      setError('Network error. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-6">
          {error ? (
            <div
              id="reset-error"
              className="p-4 text-sm text-destructive border border-destructive/20 rounded-md bg-destructive/10"
              role="alert"
            >
              {error}
            </div>
          ) : null}
          <div className="grid gap-2">
            <Label htmlFor="password">New password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              placeholder={CURRENT_CLIENT.placeholderPassword}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              aria-invalid={errorField === "password" || undefined}
              aria-describedby={error ? "reset-error" : undefined}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="passwordConfirm">Confirm new password</Label>
            <Input
              id="passwordConfirm"
              type="password"
              autoComplete="new-password"
              placeholder="Confirm new password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              required
              disabled={isLoading}
              aria-invalid={errorField === "confirm" || undefined}
              aria-describedby={error ? "reset-error" : undefined}
            />
          </div>
          <AnimatedLoginButton
            disabled={isLoading}
            isLoading={isLoading}
            mode="passwordResetConfirm"
          />
          <AuthBackToSignInLink label="back" />
        </div>
      </form>
    </div>
  )
}
